<div class="relative flex flex-col h-full w-full overflow-auto">
    <div class="flex flex-auto flex-col h-full max-h-full w-full max-w-full overflow-hidden bg-transparent">
        @if (messages() !== null && messages().length > 0)
        {
            <div
                class="flex flex-col overflow-y-auto overscroll-y-contain grow"
                (scroll)="handleScroll($event)"
                #messagesContainer
            >
                <div class="flex flex-auto shrink flex-col p-4">
                    @for (
                        message of messages();
                        track message.id;
                        let i = $index;
                    )
                    {
                        <div class="message-container flex flex-col my-2 relative"
                             [ngClass]="{
                                'items-end': message.role === messageRole.USER,
                                'items-start': message.role === messageRole.ASSISTANT
                             }">
                            <!-- Message header with role -->
                            <div class="text-xs text-gray-500 mb-1 px-2">
                                @if (message.role === messageRole.ASSISTANT)
                                {
                                    <span>Asistente</span>
                                }
                                @else
                                {
                                    <span>{{ currentUser().name || 'Tú' }}</span>
                                }
                                <span class="ml-2 text-xs">{{ message.timestamp | date:'shortTime' }}</span>
                                @if (message.isEdited)
                                {
                                    <span class="ml-1 text-xs italic">(editado)</span>
                                }
                            </div>

                            <!-- Message body -->
                            <div class="message-bubble px-4 py-2 max-w-[80%]"
                                 [ngClass]="{
                                    'user-message': message.role === messageRole.USER,
                                    'assistant-message': message.role === messageRole.ASSISTANT
                                 }">
                                @if (message.role === messageRole.ASSISTANT && isTyping() && i === messages().length - 1)
                                {
                                    <div [innerHTML]="typedMessage()"></div>
                                }
                                @else
                                {
                                    <div [innerHTML]="renderMarkdown(message.message)"></div>
                                }
                            </div>

                            <!-- Notes buttons (only for assistant messages) -->
                            @if (message.role === messageRole.ASSISTANT && hasValidNotes(message))
                            {
                                <div class="mt-2 mb-1 flex flex-wrap gap-2">
                                    <div class="text-xs text-gray-600 w-full mb-1">Notas relacionadas:</div>
                                    @for (note of message.meta.notes; track note.id)
                                    {
                                        <button 
                                            class="inline-flex items-center px-2 py-1 text-xs bg-blue-100 hover:bg-blue-200 text-blue-800 rounded-md transition-colors duration-200 cursor-pointer"
                                            (click)="onNavigateToNote(note.id, note.name)"
                                            [title]="'Ver nota: ' + note.name"
                                        >
                                            <mat-icon class="text-sm mr-1" style="font-size: 14px; width: 14px; height: 14px;">description</mat-icon>
                                            <span class="truncate max-w-[120px]">{{ note.name }}</span>
                                        </button>
                                    }
                                </div>
                            }

                            <!-- Message actions -->
                            @if (message.role === messageRole.USER)
                            {
                                <div class="message-actions flex space-x-1 mt-1 mr-1">
                                    @if (showEditFunctionality())
                                    {
                                        <button mat-icon-button size="small" (click)="startEditMessage(message)">
                                            <mat-icon class="text-gray-500 text-sm">edit</mat-icon>
                                        </button>
                                    }

                                    @if (showFavoriteFunctionality())
                                    {
                                        <button mat-icon-button size="small" (click)="toggleFavorite(message)">
                                            <mat-icon class="text-gray-500 text-sm" [ngClass]="{'text-amber-500': message.isFavorite}">
                                                {{ message.isFavorite ? 'star' : 'star_outline' }}
                                            </mat-icon>
                                        </button>
                                    }
                                </div>
                            }
                        </div>
                    }

                    <!-- Response spinner -->
                    @if (showSpinner())
                    {
                        <div class="flex items-start my-2">
                            @if (typeSpinner() === spinnerType.BUBBLE)
                            {
                                <div class="flex space-x-1 p-2 assistant-message">
                                    <div class="h-2 w-2 bg-gray-500 rounded-full animate-bounce"></div>
                                    <div class="h-2 w-2 bg-gray-500 rounded-full animate-bounce [animation-delay:0.2s]"></div>
                                    <div class="h-2 w-2 bg-gray-500 rounded-full animate-bounce [animation-delay:0.4s]"></div>
                                </div>
                            }
                            @else
                            {
                                <div class="p-2 assistant-message">
                                    <div class="h-4 w-4 border-2 border-t-transparent border-gray-500 rounded-full animate-spin"></div>
                                </div>
                            }
                        </div>
                    }
                </div>
            </div>

            <!-- Message field -->
            <div class="flex items-end border-t bg-gray-50 pt-4 px-4">
                @if (editingMessage())
                {
                    <div class="w-full flex items-center text-sm text-blue-600 mb-6">
                        <mat-icon class="text-sm mr-1">edit</mat-icon>
                        <span>Editando mensaje</span>
                        <button mat-icon-button size="small" class="ml-auto" (click)="cancelEdit()">
                            <mat-icon class="text-gray-500 text-sm">close</mat-icon>
                        </button>
                    </div>
                }

                <mat-form-field class="w-full">
                    <textarea
                        matInput
                        cdkTextareaAutosize
                        placeholder="Escribe un mensaje..."
                        [ngModel]="messageInputValue()"
                        (ngModelChange)="messageInputValue.set($event)"
                        (keydown.enter)="onEnterKey($event)"
                        #messageInput
                    ></textarea>
                </mat-form-field>

                <div class="my-px ml-2 flex h-11 items-center mb-5">
                    @if (favoriteTemplates().length > 0 && showFavoriteFunctionality())
                    {
                        <button
                            mat-icon-button
                            type="button"
                            [matMenuTriggerFor]="favoritesMenu"
                        >
                            <mat-icon>star</mat-icon>
                        </button>

                        <mat-menu #favoritesMenu="matMenu">
                            <div class="favorite-templates p-1">
                                @for (template of favoriteTemplates(); track template.id)
                                {
                                    <div class="favorite-item flex items-center relative" [class.pr-10]="showDeleteFavorites()">
                                        <button
                                            mat-menu-item
                                            class="flex items-center w-full"
                                            (click)="useTemplate(template)"
                                            [matTooltip]="template.content"
                                            matTooltipClass="favorite-tooltip"
                                            [matTooltipPosition]="'right'"
                                        >
                                            <mat-icon class="mr-2">text_snippet</mat-icon>
                                            <span>{{ template.label || template.content }}</span>
                                        </button>

                                        @if (showDeleteFavorites())
                                        {
                                            <button
                                                mat-icon-button
                                                type="button"
                                                class="absolute right-1 delete-favorite-btn"
                                                (click)="removeFavorite(template.id, $event)"
                                                [matTooltip]="'Eliminar favorito'"
                                            >
                                                <mat-icon class="text-red-500 text-sm">close</mat-icon>
                                            </button>
                                        }
                                    </div>
                                }
                            </div>
                        </mat-menu>
                    }

                    <button
                        mat-icon-button
                        type="button"
                        (click)="handleSendMessage()"
                        [disabled]="!messageInputValue().trim()"
                    >
                        <mat-icon>send</mat-icon>
                    </button>
                </div>
            </div>
        }
        @else
        {
            <div class="flex flex-col items-center justify-center h-full p-8 text-center">
                <mat-icon class="text-6xl text-gray-300 mb-4">chat</mat-icon>
                <h3 class="text-xl font-medium mb-2">Comienza una conversación</h3>
                <p class="text-gray-500">Escribe un mensaje para iniciar una conversación con el asistente IA</p>
            </div>
            <!-- start of write section -->
            <div class="flex items-end border-t bg-gray-50 pt-4 px-4">
                <mat-form-field class="w-full">
                    <textarea
                        matInput
                        cdkTextareaAutosize
                        placeholder="Escribe un mensaje..."
                        [ngModel]="messageInputValue()"
                        (ngModelChange)="messageInputValue.set($event)"
                        (keydown.enter)="onEnterKey($event)"
                        #messageInput
                    ></textarea>
                </mat-form-field>

                <div class="my-px ml-2 flex h-11 items-center mb-5">
                    @if (favoriteTemplates() !== null && favoriteTemplates().length > 0 && showFavoriteFunctionality())
                    {
                        <button
                            mat-icon-button
                            type="button"
                            [matMenuTriggerFor]="favoritesMenu"
                        >
                            <mat-icon>star</mat-icon>
                        </button>

                        <mat-menu #favoritesMenu="matMenu">
                            <div class="favorite-templates p-1">
                                @for (template of favoriteTemplates(); track template.id)
                                {
                                    <div class="favorite-item flex items-center relative" [class.pr-10]="showDeleteFavorites()">
                                        <button
                                            mat-menu-item
                                            class="flex items-center w-full"
                                            (click)="useTemplate(template)"
                                            [matTooltip]="template.content"
                                            matTooltipClass="favorite-tooltip"
                                            [matTooltipPosition]="'right'"
                                        >
                                            <mat-icon class="mr-2">text_snippet</mat-icon>
                                            <span>{{ template.label || template.content }}</span>
                                        </button>

                                        @if (showDeleteFavorites())
                                        {
                                            <button
                                                mat-icon-button
                                                type="button"
                                                class="absolute right-1 delete-favorite-btn"
                                                (click)="removeFavorite(template.id, $event)"
                                                [matTooltip]="'Eliminar favorito'"
                                            >
                                                <mat-icon class="text-red-500 text-sm">close</mat-icon>
                                            </button>
                                        }
                                    </div>
                                }
                            </div>
                        </mat-menu>
                    }

                    <button
                        mat-icon-button
                        type="button"
                        (click)="handleSendMessage()"
                        [disabled]="!messageInputValue().trim()"
                    >
                        <mat-icon>send</mat-icon>
                    </button>
                </div>
            </div>
        }
    </div>
</div>
